import React, { useMemo } from 'react';
import GenericListPage from '../GenericListPage';
import InventoryTransferForm from './InventoryTransferForm';
import { Link, useNavigate } from 'react-router-dom';
import useApi from '../../hooks/useApi';

const InventoryTransfersPage = () => {
  const api = useApi();
  const navigate = useNavigate();

  const columns = useMemo(() => [
    { header: 'Ref', key: '_id', render: r => r.reference || (r._id ?? '—') },
    { header: 'From', key: 'fromLocation', render: r => `${r.fromLocation?.type || '—'} ${r.fromName || ''}` },
    { header: 'To', key: 'toLocation', render: r => `${r.toLocation?.type || '—'} ${r.toName || ''}` },
    { header: 'Items', key: 'items', render: r => Array.isArray(r.items) ? r.items.length : (r.itemsCount ?? '—') },
    { header: 'Notes', key: 'notes', render: r => r.notes || '—' },
    { header: 'Status', key: 'status', render: r => r.status || '—' },
    { header: 'Created', key: 'createdAt', render: r => r.createdAt ? new Date(r.createdAt).toLocaleString() : '—' },
  ], []);

  const filterConfig = useMemo(() => [
    { name: 'status', type: 'select', label: 'Status', options: [{ value: '', label: 'Any' }, { value: 'Pending', label: 'Pending' }, { value: 'In-Transit', label: 'In-Transit' }, { value: 'Completed', label: 'Completed' }, { value: 'Cancelled', label: 'Cancelled' }] },
    { name: 'fromType', type: 'select', label: 'From Type', options: [{ value: '', label: 'Any' }, { value: 'store', label: 'Store' }, { value: 'warehouse', label: 'Warehouse' }] },
    { name: 'toType', type: 'select', label: 'To Type', options: [{ value: '', label: 'Any' }, { value: 'store', label: 'Store' }, { value: 'warehouse', label: 'Warehouse' }] },
    { name: 'fromLocationId', type: 'text', label: 'From ID' },
    { name: 'toLocationId', type: 'text', label: 'To ID' }
  ], []);

  const headerActions = (
    <div className="flex items-center gap-2">
      <Link to="/inventory-transfers/create">
        <button className="bg-green-600 text-white px-3 py-1 rounded text-sm">+ New Transfer</button>
      </Link>
      <Link to="/inventory-transfers/export">
        <button className="bg-indigo-600 text-white px-3 py-1 rounded text-sm">Export</button>
      </Link>
    </div>
  );

  const handleDownloadCsv = async (currentFilters = {}) => {
    try {
      const resp = await api.get('inventory-transfers', { queryParams: { ...currentFilters, noPagination: true } });
      const rows = Array.isArray(resp) ? resp : (Array.isArray(resp?.data?.data) ? resp.data.data : (Array.isArray(resp?.data) ? resp.data : []));
      if (!rows || rows.length === 0) {
        alert('No transfers to export.');
        return;
      }
      const cols = ['_id','fromLocation.type','fromLocation.id','toLocation.type','toLocation.id','itemsCount','status','notes','createdAt'];
      const escape = v => `"${String(v ?? '').replace(/"/g,'""')}"`;
      const header = cols.map(c => escape(c)).join(',');
      const body = rows.map(r => cols.map(k => {
        const val = k.includes('.') ? k.split('.').reduce((o,p)=>o?o[p]:undefined,r) : r[k];
        return escape(val);
      }).join(',')).join('\r\n');
      const csv = `${header}\r\n${body}`;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory-transfers-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed', err);
      alert('Export failed. See console.');
    }
  };

  const handleDispatch = async (transfer, refreshList) => {
    if (!window.confirm(`Dispatch transfer ${transfer.transferNumber || transfer._id}?`)) {
      return;
    }

    try {
      await api.post(`inventory-transfers/${transfer._id}/dispatch`, {});
      alert('Transfer dispatched successfully. Inventory log has been updated.');
      if (typeof refreshList === 'function') {
        refreshList();
      }
    } catch (err) {
      console.error('Dispatch failed', err);
      alert(err?.message || 'Dispatch failed.');
    }
  };

  const handleCompleteTransit = async (transfer, refreshList) => {
    if (!window.confirm(`Complete transit for transfer ${transfer.transferNumber || transfer._id}?`)) {
      return;
    }

    try {
      await api.post(`inventory-transfers/${transfer._id}/receive`, {});
      alert('Transit completed successfully. Inventory log has been updated.');
      if (typeof refreshList === 'function') {
        refreshList();
      }
    } catch (err) {
      console.error('Complete transit failed', err);
      alert(err?.message || 'Failed to complete transit.');
    }
  };

  const rowActions = (transfer, fetchData) => (
    <div className="flex items-center gap-2">
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/inventory-transfers/${transfer._id}`);
        }}
        className="text-slate-600 hover:text-slate-900"
        title="View Details"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12H9m12 0A9 9 0 1112 3a9 9 0 019 9z"></path>
        </svg>
      </button>
      {transfer.status === 'Pending' && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDispatch(transfer, fetchData);
          }}
          className="text-emerald-600 hover:text-emerald-900"
          title="Dispatch"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M13 5l7 7-7 7"></path>
          </svg>
        </button>
      )}
      {transfer.status === 'In-Transit' && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleCompleteTransit(transfer, fetchData);
          }}
          className="text-emerald-600 hover:text-emerald-900"
          title="Complete Transit"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </button>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/stock-history?relatedDocId=${transfer._id}`);
        }}
        className="text-indigo-600 hover:text-indigo-900"
        title="View Inventory Log"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2a4 4 0 014-4h4m0 0l-3-3m3 3l-3 3M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z"></path>
        </svg>
      </button>
    </div>
  );

  return (
    <GenericListPage
      title="Inventory Transfers"
      permission="manage_inventory_transfers"
      entityEndpoint="inventory-transfers"
      columns={columns}
      FormComponent={InventoryTransferForm}
      filterConfig={filterConfig}
      headerActions={headerActions}
      rowActions={rowActions}
      onDownloadCsv={handleDownloadCsv}
      defaultQuery={{ status: 'Pending', fromType: 'store' }}
    />
  );
};

export default InventoryTransfersPage;